import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import { useWs } from '@/shared/hooks/use-ws';
import { HEARTBEAT_INTERVAL_MS } from '@/shared/hooks/use-ws/constants';
import { MessageAction, MessageType } from '@/shared/hooks/use-ws/types';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import { LogDevMessageKinds } from '@/shared/service/logger/types';

const SCREENTIME_INTERVAL_MS = 5_000;

const logOnline = ErrorLogger.makeLogDevMessage<boolean>(
  LogDevMessageKinds.online,
  () => 'APP IS ACTIVE'
);
const logOffline = ErrorLogger.makeLogDevMessage<boolean>(
  LogDevMessageKinds.offline,
  () => 'APP IS INACTIVE'
);

export const useScreenTimeTracking = () => {
  const ws = useWs();

  const screentimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isActiveWebApp, setIsActiveWebApp] = useState(WebApp.isActive);
  const [isVisibilityVisible, setIsVisibilityVisible] = useState(
    document.visibilityState === 'visible'
  );

  const isAppActive = isActiveWebApp && isVisibilityVisible;

  useEffect(() => {
    const handleActivatedEvent = () => {
      setIsActiveWebApp(true);
    };

    const handleDeactivatedEvent = () => {
      setIsActiveWebApp(false);
    };

    const handleVisibilityChangeEvent = () => {
      if (document.visibilityState === 'visible') {
        setIsVisibilityVisible(true);
      } else {
        setIsVisibilityVisible(false);
      }
    };

    WebApp.onEvent('activated', handleActivatedEvent);
    WebApp.onEvent('deactivated', handleDeactivatedEvent);

    window.addEventListener('visibilitychange', handleVisibilityChangeEvent, false);

    return () => {
      WebApp.offEvent('activated', handleActivatedEvent);
      WebApp.offEvent('deactivated', handleDeactivatedEvent);

      window.removeEventListener('visibilitychange', handleVisibilityChangeEvent);
    };
  }, []);

  const stopInterval = useCallback(() => {
    if (screentimeIntervalRef.current) {
      clearInterval(screentimeIntervalRef.current);
      screentimeIntervalRef.current = null;
    }
  }, []);

  const sendScreentime = useCallback(() => {
    if (import.meta.env.DEV) return;
    ws.sendMessage(MessageType.SCREENTIME, MessageAction.EXECUTE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendHeartbeat = useCallback(() => {
    ws.sendMessage(MessageType.HEARTBEAT, MessageAction.EXECUTE);
  }, [ws]);

  useEffect(() => {
    if (isAppActive) {
      logOnline(true);
      stopInterval();

      screentimeIntervalRef.current = setInterval(() => {
        sendScreentime();
      }, SCREENTIME_INTERVAL_MS);
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    } else {
      logOffline(false);
      stopInterval();
    }

    return () => {
      stopInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppActive]);
};
